import { format } from 'date-fns';
import PocketBase from 'pocketbase';

const POCKETBASE_URL = 'https://legal-terms-and-conditions-bpasolutions.pockethost.io';
const AUDIT_COLLECTION = 'legal_audit_logs';

let pb = null;
let initPromise = null;

const connectionStatus = {
  status: 'disconnected',
  url: POCKETBASE_URL,
  isConfigured: !!POCKETBASE_URL,
  lastError: null,
  isAuthenticated: false,
};

const buildPocketBaseFileUrl = (record, filename) => {
  if (!record || !filename) return '';
  if (pb?.files && typeof pb.files.getURL === 'function') {
    return pb.files.getURL(record, filename);
  }
  if (pb?.files && typeof pb.files.getUrl === 'function') {
    return pb.files.getUrl(record, filename);
  }
  if (typeof pb?.getFileUrl === 'function') {
    return pb.getFileUrl(record, filename);
  }
  const collection = record.collectionId || record.collectionName || 'legal_branding_profiles';
  return `${POCKETBASE_URL}/api/files/${encodeURIComponent(collection)}/${encodeURIComponent(record.id)}/${encodeURIComponent(filename)}`;
};

function slugify(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function toPocketBaseDateTime(value) {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00.000Z`).toISOString();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
}

async function sha256Hex(value = '') {
  const cryptoApi = globalThis?.crypto;
  if (!cryptoApi?.subtle || typeof TextEncoder === 'undefined') {
    throw new Error('Secure SHA-256 hashing is unavailable in this browser. Publication was not performed.');
  }
  const encoded = new TextEncoder().encode(String(value));
  const digest = await cryptoApi.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function toDateInputValue(value) {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
}

function toDateTimeLocalInputValue(value) {
  if (!value) return '';
  try {
    const date = new Date(value);
    if (!isNaN(date.getTime())) return date.toISOString().slice(0, 16);
  } catch {
    return '';
  }
  return '';
}

function getPocketBaseErrorMessage(error) {
  const response = error?.response || {};
  const fieldErrors = response?.data || {};
  const details = Object.entries(fieldErrors)
    .map(([field, issue]) => `${field}: ${issue?.message || issue}`)
    .join(';');
  return details || response?.message || error?.message || 'Request failed.';
}

const auditActionToPocketBase = {
  'Document Created': 'created',
  'Draft Saved': 'draft_saved',
  'Version Published': 'published',
  'Version Duplicated': 'duplicated',
  'Public Link Copied': 'public_link_copied',
  'Visibility Updated': 'settings_updated',
};

const auditActionFromPocketBase = {
  created: 'Document Created',
  draft_saved: 'Draft Saved',
  published: 'Version Published',
  duplicated: 'Version Duplicated',
  public_link_copied: 'Public Link Copied',
  visibility_updated: 'Visibility Updated',
  settings_updated: 'Settings Updated',
};

if (connectionStatus.isConfigured) {
  try {
    const url = new URL(POCKETBASE_URL);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      throw new Error('PocketBase URL must use http or https protocol');
    }
  } catch (e) {
    connectionStatus.lastError = e.message;
    connectionStatus.status = 'error';
  }
}

let lastAuthRecord = null;
let lastAuthRefreshAt = 0;

const initialisePocketBase = async () => {
  if (!connectionStatus.isConfigured) return null;
  if (connectionStatus.status === 'connected' && pb) return pb;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      connectionStatus.status = 'connecting';
      connectionStatus.lastError = null;

      if (!pb) {
        pb = new PocketBase(POCKETBASE_URL);
      }

      await pb.health.check();

      if (pb.authStore.isValid) {
        try {
          const authData = await pb.collection('users').authRefresh();
          lastAuthRecord = authData.record;
          lastAuthRefreshAt = Date.now();
        } catch (authErr) {
          pb.authStore.clear();
          lastAuthRecord = null;
          lastAuthRefreshAt = 0;
        }
      }

      connectionStatus.status = 'connected';
      connectionStatus.isAuthenticated = pb.authStore.isValid;
      return pb;
    } catch (error) {
      connectionStatus.status = 'error';
      connectionStatus.lastError = getPocketBaseErrorMessage(error);
      pb = null;
      throw new Error(`Backend unavailable: ${connectionStatus.lastError}`);
    } finally {
      initPromise = null;
    }
  })();

  return initPromise;
};

const requirePocketBase = async (operationName) => {
  if (!connectionStatus.isConfigured) {
    throw new Error(`Backend not configured. Cannot perform: ${operationName}`);
  }
  const client = await initialisePocketBase().catch(() => null);
  if (!client || connectionStatus.status !== 'connected') {
    throw new Error(
      `Backend unavailable. Cannot perform: ${operationName}. Reason: ${connectionStatus.lastError || 'Health check failed'}`
    );
  }
  return client;
};

async function requireFreshAuth(allowedRoles = []) {
  const client = await requirePocketBase('requireFreshAuth');
  if (!client.authStore.isValid) {
    throw new Error('Your session has expired. Please sign in again.');
  }
  try {
    const now = Date.now();
    if (!lastAuthRecord || now - lastAuthRefreshAt > 60000) {
      const authData = await client.collection('users').authRefresh();
      lastAuthRecord = authData.record;
      lastAuthRefreshAt = now;
    }
    const user = client.authStore.model || lastAuthRecord;
    if (!user) {
      throw new Error('Your session has expired. Please sign in again.');
    }
    if (user.active === false) {
      throw new Error('This user account is inactive.');
    }
    if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
      throw new Error('You do not have permission to perform this action.');
    }
    return user;
  } catch (error) {
    if (error.message === 'You do not have permission to perform this action.') {
      throw error;
    }
    if (error.message === 'This user account is inactive.') {
      client.authStore.clear();
      lastAuthRecord = null;
      lastAuthRefreshAt = 0;
      throw error;
    }
    client.authStore.clear();
    lastAuthRecord = null;
    lastAuthRefreshAt = 0;
    throw new Error('Your session has expired. Please sign in again.');
  }
}

const documentTypeMapToPocketBase = {
  'Service Terms & Conditions': 'service_terms',
  'Website Design Terms': 'website_terms',
  'Software Application Terms': 'software_terms',
  'SaaS Terms': 'saas_terms',
  'Support & Maintenance Policies': 'support_policy',
  'Licence Terms': 'licence_terms',
  'Privacy Policies': 'privacy_policy',
  'Data Sharing Policies': 'data_sharing_policy',
  'Refund and Cancellation Policies': 'refund_policy',
  'Disclaimers': 'disclaimer',
  'Custom': 'custom',
};

const documentTypeMapFromPocketBase = {
  service_terms: 'Service Terms & Conditions',
  website_terms: 'Website Design Terms',
  software_terms: 'Software Application Terms',
  saas_terms: 'SaaS Terms',
  support_policy: 'Support & Maintenance Policies',
  licence_terms: 'Licence Terms',
  privacy_policy: 'Privacy Policies',
  data_sharing_policy: 'Data Sharing Policies',
  refund_policy: 'Refund and Cancellation Policies',
  disclaimer: 'Disclaimers',
  custom: 'Custom',
};

const mapStatusFromPocketBase = (pbStatus) => {
  const statusMap = {
    draft: 'Draft',
    published: 'Published',
    archived: 'Archived',
    superseded: 'Superseded',
    withdrawn: 'Withdrawn',
    scheduled: 'Scheduled',
  };
  return statusMap[pbStatus?.toLowerCase()] || pbStatus;
};

const mapStatusToPocketBase = (uiStatus) => {
  const statusMap = {
    Draft: 'draft',
    Published: 'published',
    Archived: 'archived',
    Superseded: 'superseded',
    Withdrawn: 'withdrawn',
    Scheduled: 'scheduled',
  };
  return statusMap[uiStatus] || uiStatus;
};

const mapVisibilityFromPocketBase = (pbVisibility) => {
  const visibilityMap = {
    public: 'Public',
    unlisted: 'Unlisted',
    private: 'Private',
    password_protected: 'Password Protected',
  };
  return visibilityMap[pbVisibility] || pbVisibility;
};

const mapVisibilityToPocketBase = (uiVisibility) => {
  const visibilityMap = {
    Public: 'public',
    Unlisted: 'unlisted',
    Private: 'private',
    'Password Protected': 'password_protected',
  };
  return visibilityMap[uiVisibility] || uiVisibility;
};

const mapDocumentFromPocketBase = (pbDoc) => {
  if (!pbDoc) return null;
  return {
    ...pbDoc,
    type: documentTypeMapFromPocketBase[pbDoc.documentType] || pbDoc.documentType,
    status: mapStatusFromPocketBase(pbDoc.status),
    visibility: mapVisibilityFromPocketBase(pbDoc.visibility),
    tags: Array.isArray(pbDoc.tags) ? pbDoc.tags.join(',') : pbDoc.tags,
    created: pbDoc.createdAt || '',
    updated: pbDoc.updatedAt || '',
    reviewDate: toDateInputValue(pbDoc.reviewDate),
  };
};

const mapVersionFromPocketBase = (pbVersion) => {
  if (!pbVersion) return null;
  return {
    ...pbVersion,
    content: pbVersion.contentMarkdown || '',
    status: mapStatusFromPocketBase(pbVersion.status),
    effectiveDate: toDateInputValue(pbVersion.effectiveDate),
    publishedDate: pbVersion.publishedAt || '',
    archivedDate: pbVersion.archivedAt || '',
    scheduledPublishDate: toDateTimeLocalInputValue(pbVersion.scheduledPublishAt),
    scheduledArchiveDate: toDateTimeLocalInputValue(pbVersion.scheduledArchiveAt),
    createdDate: pbVersion.createdAt || pbVersion.created || '',
    updatedDate: pbVersion.updatedAt || pbVersion.updated || '',
  };
};

const mapTemplateFromPocketBase = (record) => {
  if (!record) return null;
  return {
    id: record.id,
    name: record.title,
    type: documentTypeMapFromPocketBase[record.documentType] || record.documentType,
    description: record.description || '',
    starterContent: record.starterContentHtml || '',
    tags: Array.isArray(record.tags) ? record.tags : [],
    brandId: record.brandId || '',
    isActive: record.isActive !== false,
    created: record.createdAt || '',
    updated: record.updatedAt || '',
  };
};

const mapPublicLink = (record) => {
  if (!record) return null;
  return {
    id: record.id,
    documentId: record.documentId,
    slug: record.slug,
    isActive: record.isActive !== false,
    expiresAt: record.expiresAt || '',
    requireEmailForDownload: record.requireEmailForDownload,
    allowPdf: record.allowPdf,
    allowWord: record.allowWord,
    allowPrint: record.allowPrint,
    url: `${window.location.origin}/#/legal/${record.slug}`,
    version: 'Current',
  };
};

const normalizeBrand = (pbRecord) => {
  if (!pbRecord) return null;
  const normalized = {
    ...pbRecord,
    name: pbRecord.name,
    tradingName: pbRecord.tradingName,
    abn: pbRecord.abn,
    acn: pbRecord.acn,
    website: pbRecord.website,
    phone: pbRecord.phone,
    email: pbRecord.email,
    legalEmail: pbRecord.legalEmail,
    supportEmail: pbRecord.supportEmail,
    address: pbRecord.address,
    footerDisclaimer: pbRecord.footerDisclaimer,
    isDefault: pbRecord.isDefault,
    isActive: pbRecord.isActive,
  };
  if (pbRecord.logo) {
    normalized.logo =
      pb && connectionStatus.status === 'connected'
        ? buildPocketBaseFileUrl(pbRecord, pbRecord.logo)
        : pbRecord.logo;
    normalized.logoFilename = pbRecord.logo;
  }
  return normalized;
};

export const legalVaultService = {
  initialisePocketBase,
  requirePocketBase,

  getConnectionStatus: () => ({ ...connectionStatus }),
  isConnected: () => connectionStatus.status === 'connected',
  isBackendConfigured: () => connectionStatus.isConfigured,

  getReviewStatus: (reviewDate) => {
    if (!reviewDate || reviewDate === '-') return null;
    const review = new Date(reviewDate);
    if (Number.isNaN(review.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    review.setHours(0, 0, 0, 0);
    const daysRemaining = Math.ceil((review.getTime() - today.getTime()) / 86400000);
    if (daysRemaining < 0) return { status: 'Overdue', color: 'bg-red-50 text-red-700 border-red-200' };
    if (daysRemaining === 0) return { status: 'Due today', color: 'bg-red-50 text-red-700 border-red-200' };
    if (daysRemaining <= 30) return { status: 'Due soon', color: 'bg-amber-50 text-amber-700 border-amber-200' };
    return null;
  },

  getCurrentUser: () => {
    if (pb && connectionStatus.status === 'connected') return pb.authStore.model || lastAuthRecord || null;
    return lastAuthRecord;
  },

  isAuthenticated: () => {
    if (pb && connectionStatus.status === 'connected') return pb.authStore.isValid;
    return false;
  },

  login: async (email, password) => {
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('login');
      try {
        const authData = await client.collection('users').authWithPassword(email, password);
        lastAuthRecord = authData.record;
        lastAuthRefreshAt = Date.now();
        connectionStatus.isAuthenticated = true;
        return authData.record;
      } catch (error) {
        if (error.status === 400) throw new Error('Invalid email or password. Please check your credentials.');
        throw new Error(getPocketBaseErrorMessage(error));
      }
    }
    throw new Error('Secure backend unavailable or not configured.');
  },

  requestPasswordReset: async (email) => {
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('requestPasswordReset');
      try {
        await client.collection('users').requestPasswordReset(email);
        return true;
      } catch (error) {
        throw new Error(getPocketBaseErrorMessage(error));
      }
    }
    throw new Error('Secure backend unavailable. Cannot reset password.');
  },

  logout: () => {
    if (pb && connectionStatus.status === 'connected') {
      pb.authStore.clear();
      lastAuthRecord = null;
      lastAuthRefreshAt = 0;
      connectionStatus.isAuthenticated = false;
    }
  },

  hasAdminAccess: () => {
    const user = legalVaultService.getCurrentUser();
    if (!user || user.active === false) return false;
    return ['owner', 'admin', 'editor'].includes(user.role);
  },

  hasSettingsAccess: () => {
    const user = legalVaultService.getCurrentUser();
    if (!user || user.active === false) return false;
    return ['owner', 'admin'].includes(user.role);
  },

  hasViewerAccess: () => {
    const user = legalVaultService.getCurrentUser();
    if (!user || user.active === false) return false;
    return user.role === 'viewer' || legalVaultService.hasAdminAccess();
  },

  getDocuments: async () => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const client = await requirePocketBase('getDocuments');
        const records = await client.collection('legal_documents').getFullList({ sort: '-createdAt' });
        return Promise.all(
          records.map(async (record) => {
            const mapped = mapDocumentFromPocketBase(record);
            const currentVersion = await legalVaultService.getCurrentVersion(record.id);
            return { ...mapped, currentVersion: currentVersion?.versionNumber || null };
          })
        );
      } catch (error) {
        console.error('Error fetching documents:', error);
        return [];
      }
    }
    return [];
  },

  getDocumentById: async (id) => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const client = await requirePocketBase('getDocumentById');
        const record = await client.collection('legal_documents').getOne(id);
        return mapDocumentFromPocketBase(record);
      } catch (error) {
        console.error('Error fetching document:', error);
        return null;
      }
    }
    return null;
  },

  getPublicDocuments: async () => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const client = await requirePocketBase('getPublicDocuments');
        const docs = await client.collection('legal_documents').getFullList({
          filter: 'visibility="public" && status="published"',
          sort: '-updatedAt',
        });
        const activeLinks = await client.collection('legal_public_links').getFullList({
          filter: 'isActive=true',
        });
        const now = new Date().toISOString();
        const validLinkMap = new Map(
          activeLinks
            .filter((link) => !link.expiresAt || link.expiresAt > now)
            .map((link) => [link.documentId, link])
        );
        const validDocs = docs.filter((doc) => validLinkMap.has(doc.id));
        const results = await Promise.all(
          validDocs.map(async (doc) => {
            try {
              const document = mapDocumentFromPocketBase(doc);
              const versions = await client.collection('legal_versions').getFullList({
                filter: `documentId="${doc.id}" && status="published"`,
                sort: '-publishedAt',
              });
              if (versions.length === 0) return null;
              const version = mapVersionFromPocketBase(versions[0]);
              const brand = await legalVaultService.getBrandingProfileForDocument(document);
              return { ...document, version, brand, isFeatured: doc.isFeatured || false };
            } catch (recordError) {
              console.warn(`Skipping invalid public document ${doc.id}:`, recordError);
              return null;
            }
          })
        );
        return results.filter(Boolean);
      } catch (error) {
        console.error('Error fetching public documents:', error);
        return [];
      }
    }
    return [];
  },

  getPublicDocumentBySlug: async (slug) => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const client = await requirePocketBase('getPublicDocumentBySlug');
        const now = new Date().toISOString();
        const link = await client.collection('legal_public_links').getFirstListItem(`slug="${slug}" && isActive=true`);
        if (!link) return null;
        if (link.expiresAt && link.expiresAt < now) return null;
        const doc = await client.collection('legal_documents').getOne(link.documentId);
        if (doc.status !== 'published') return null;
        if (doc.visibility !== 'public' && doc.visibility !== 'unlisted') return null;
        const versions = await client.collection('legal_versions').getFullList({
          filter: `documentId="${doc.id}" && status="published" && isCurrent=true`,
          sort: '-publishedAt',
          limit: 1,
        });
        if (versions.length === 0) return null;
        const version = mapVersionFromPocketBase(versions[0]);
        const brand = await legalVaultService.getBrandingProfileForDocument(doc);
        return { ...mapDocumentFromPocketBase(doc), version, brand };
      } catch (error) {
        console.error('Error fetching public document by slug:', error);
        return null;
      }
    }
    return null;
  },

  getPublicVersionById: async (documentId, versionId) => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const client = await requirePocketBase('getPublicVersionById');
        const version = await client.collection('legal_versions').getOne(versionId);
        if (!['published', 'superseded'].includes(version.status)) return null;
        if (!version.isImmutable) return null;
        if (version.documentId !== documentId) return null;
        const doc = await client.collection('legal_documents').getOne(documentId);
        if (!['public', 'unlisted'].includes(doc.visibility) || doc.status !== 'published') return null;
        const brand = await legalVaultService.getBrandingProfileForDocument(doc);
        return { ...mapDocumentFromPocketBase(doc), version: mapVersionFromPocketBase(version), brand };
      } catch (error) {
        console.error('Error fetching public version:', error);
        return null;
      }
    }
    return null;
  },

  getPublicVersionByVersionId: async (versionId) => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const client = await requirePocketBase('getPublicVersionByVersionId');
        const version = await client.collection('legal_versions').getOne(versionId);
        if (!['published', 'superseded'].includes(version.status)) {
          throw new Error('This version is not publicly available.');
        }
        if (!version.isImmutable) {
          throw new Error('This version is not an immutable published record.');
        }
        const doc = await client.collection('legal_documents').getOne(version.documentId);
        if (!doc) {
          throw new Error('Document not found.');
        }
        if (!['public', 'unlisted'].includes(doc.visibility) || doc.status !== 'published') {
          throw new Error('This document is not publicly available.');
        }
        const brand = await legalVaultService.getBrandingProfileForDocument(doc);
        return { ...mapDocumentFromPocketBase(doc), version: mapVersionFromPocketBase(version), brand };
      } catch (error) {
        console.error('Error fetching version by ID:', error);
        throw error;
      }
    }
    throw new Error('Backend not configured.');
  },

  createDocument: async (documentData) => {
    if (!documentData.title?.trim()) throw new Error('Document title is required.');
    if (!documentData.type) throw new Error('Document type is required.');
    const finalSlug = documentData.slug?.trim() || slugify(documentData.title);
    if (!finalSlug) throw new Error('Document URL slug is required.');
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('createDocument');
      try {
        await requireFreshAuth(['owner', 'admin', 'editor']);
        const now = new Date().toISOString();
        const pbData = {
          title: documentData.title.trim(),
          slug: finalSlug,
          documentType: documentTypeMapToPocketBase[documentData.type] || documentData.type,
          status: mapStatusToPocketBase(documentData.status || 'Draft'),
          visibility: mapVisibilityToPocketBase(documentData.visibility || 'Private'),
          tags: documentData.tags
            ? documentData.tags
                .split(',')
                .map((tag) => tag.trim())
                .filter((tag) => tag)
            : [],
          createdAt: now,
          updatedAt: now,
        };
        if (documentData.brandId) pbData.brandId = documentData.brandId;
        if (documentData.seoTitle) pbData.seoTitle = documentData.seoTitle;
        if (documentData.seoDescription) pbData.seoDescription = documentData.seoDescription;
        if (documentData.internalNotes) pbData.internalNotes = documentData.internalNotes;
        if (documentData.reviewDate) pbData.reviewDate = toPocketBaseDateTime(documentData.reviewDate);
        const record = await client.collection('legal_documents').create(pbData);
        return mapDocumentFromPocketBase(record);
      } catch (error) {
        console.error('Error creating document:', error);
        console.error('PocketBase error details:', error?.response);
        throw new Error(getPocketBaseErrorMessage(error));
      }
    }
    throw new Error('Backend not configured.');
  },

  updateDocument: async (id, updates) => {
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('updateDocument');
      try {
        await requireFreshAuth(['owner', 'admin', 'editor']);
        const pbUpdates = { updatedAt: new Date().toISOString() };
        if (updates.title) pbUpdates.title = updates.title.trim();
        if (updates.slug) pbUpdates.slug = updates.slug.trim();
        if (updates.type) pbUpdates.documentType = documentTypeMapToPocketBase[updates.type] || updates.type;
        if (updates.status) pbUpdates.status = mapStatusToPocketBase(updates.status);
        if (updates.visibility) pbUpdates.visibility = mapVisibilityToPocketBase(updates.visibility);
        if (updates.tags !== undefined)
          pbUpdates.tags = updates.tags
            ? updates.tags
                .split(',')
                .map((tag) => tag.trim())
                .filter((tag) => tag)
            : [];
        if (updates.brandId) pbUpdates.brandId = updates.brandId;
        if (updates.reviewDate !== undefined) pbUpdates.reviewDate = toPocketBaseDateTime(updates.reviewDate);
        if (updates.seoTitle !== undefined) pbUpdates.seoTitle = updates.seoTitle;
        if (updates.seoDescription !== undefined) pbUpdates.seoDescription = updates.seoDescription;
        if (updates.internalNotes !== undefined) pbUpdates.internalNotes = updates.internalNotes;
        const record = await client.collection('legal_documents').update(id, pbUpdates);
        return mapDocumentFromPocketBase(record);
      } catch (error) {
        throw new Error(getPocketBaseErrorMessage(error));
      }
    }
    throw new Error('Backend not configured.');
  },

  updatePublishedVisibility: async (documentId, visibility) => {
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('updatePublishedVisibility');
      try {
        await requireFreshAuth(['owner', 'admin']);
        const document = await client.collection('legal_documents').getOne(documentId);
        if (document.status !== 'published') {
          throw new Error(
            'Visibility can only be updated through this action after the document has been published.'
          );
        }
        const mappedVisibility = mapVisibilityToPocketBase(visibility);
        const validVisibilities = ['public', 'unlisted', 'private', 'password_protected'];
        if (!validVisibilities.includes(mappedVisibility)) {
          throw new Error('Invalid visibility value.');
        }
        const now = new Date().toISOString();
        const updatedRecord = await client
          .collection('legal_documents')
          .update(documentId, { visibility: mappedVisibility, updatedAt: now });
        if (mappedVisibility === 'public' || mappedVisibility === 'unlisted') {
          await legalVaultService.ensurePublicLinkForDocument(documentId);
        } else {
          await legalVaultService.deactivatePublicLinksForDocument(documentId);
        }
        await legalVaultService.addAuditEntry({
          action: 'Visibility Updated',
          documentId,
          documentTitle: document.title,
          version: document.currentVersion || '',
          details: { from: document.visibility, to: visibility },
        });
        return mapDocumentFromPocketBase(updatedRecord);
      } catch (error) {
        throw new Error(getPocketBaseErrorMessage(error));
      }
    }
    throw new Error('Backend not configured.');
  },

  duplicateDocument: async (documentId) => {
    const doc = await legalVaultService.getDocumentById(documentId);
    if (!doc) throw new Error('Document not found');
    const currentVersion = await legalVaultService.getCurrentVersion(documentId);
    const newDoc = await legalVaultService.createDocument({
      title: `${doc.title} (Copy)`,
      type: doc.type,
      slug: `${doc.slug}-copy-${Date.now()}`,
      tags: doc.tags,
      brandId: doc.brandId,
      visibility: 'Private',
      reviewDate: doc.reviewDate,
    });
    if (currentVersion) {
      await legalVaultService.createVersion(newDoc.id, {
        versionNumber: 'v1.0',
        status: 'Draft',
        content: currentVersion.content,
        changeSummary: `Duplicated from ${doc.title}`,
      });
    }
    return newDoc;
  },

  duplicateVersionAsDraft: async (versionId) => {
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('duplicateVersionAsDraft');
      try {
        await requireFreshAuth(['owner', 'admin', 'editor']);
        const sourceVersion = await client.collection('legal_versions').getOne(versionId);
        if (!sourceVersion) {
          throw new Error('Published version could not be found.');
        }
        const allVersions = await client.collection('legal_versions').getFullList({
          filter: `documentId="${sourceVersion.documentId}"`,
        });
        const versionNumbers = allVersions.map((v) => {
          const match = v.versionNumber?.match(/^v(\d+)\./);
          return match ? parseInt(match[1], 10) : 0;
        });
        const maxMajor = Math.max(...versionNumbers, 0);
        const newVersionNumber = `v${maxMajor + 1}.0`;
        const newVersion = await legalVaultService.createVersion(sourceVersion.documentId, {
          versionNumber: newVersionNumber,
          status: 'Draft',
          content: sourceVersion.contentMarkdown || '',
          changeSummary: `Draft created from ${sourceVersion.versionNumber}`,
          isCurrent: false,
          isImmutable: false,
        });
        await legalVaultService.addAuditEntry({
          action: 'Version Duplicated',
          documentId: sourceVersion.documentId,
          versionId: newVersion.id,
          version: newVersionNumber,
        });
        return newVersion;
      } catch (error) {
        throw new Error(getPocketBaseErrorMessage(error));
      }
    }
    throw new Error('Backend not configured.');
  },

  getVersions: async (documentId) => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const client = await requirePocketBase('getVersions');
        const records = await client.collection('legal_versions').getFullList({
          filter: `documentId="${documentId}"`,
          sort: '-createdAt',
        });
        return records.map(mapVersionFromPocketBase);
      } catch (error) {
        return [];
      }
    }
    return [];
  },

  getVersionById: async (id) => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const client = await requirePocketBase('getVersionById');
        const record = await client.collection('legal_versions').getOne(id);
        return mapVersionFromPocketBase(record);
      } catch (error) {
        return null;
      }
    }
    return null;
  },

  getCurrentVersion: async (documentId) => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const client = await requirePocketBase('getCurrentVersion');
        const versions = await client.collection('legal_versions').getFullList({
          filter: `documentId="${documentId}"`,
          sort: '-createdAt',
        });
        if (versions.length === 0) return null;
        const currentVersion = versions.find((v) => v.isCurrent);
        if (currentVersion) return mapVersionFromPocketBase(currentVersion);
        const publishedVersions = versions.filter((v) => v.status === 'published');
        if (publishedVersions.length > 0) return mapVersionFromPocketBase(publishedVersions[0]);
        return mapVersionFromPocketBase(versions[0]);
      } catch (error) {
        return null;
      }
    }
    return null;
  },

  createVersion: async (documentId, versionData) => {
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('createVersion');
      try {
        await requireFreshAuth(['owner', 'admin', 'editor']);
        const now = new Date().toISOString();
        const pbData = {
          documentId,
          versionNumber: versionData.versionNumber || 'v1.0',
          status: mapStatusToPocketBase(versionData.status || 'Draft'),
          contentMarkdown: versionData.content || '',
          changeSummary: versionData.changeSummary || '',
          isCurrent: versionData.isCurrent || false,
          isImmutable: versionData.isImmutable || false,
          createdAt: now,
          updatedAt: now,
        };
        const record = await client.collection('legal_versions').create(pbData);
        return mapVersionFromPocketBase(record);
      } catch (error) {
        throw new Error(getPocketBaseErrorMessage(error));
      }
    }
    throw new Error('Backend not configured.');
  },

  updateVersion: async (versionId, updates) => {
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('updateVersion');
      try {
        await requireFreshAuth(['owner', 'admin', 'editor']);
        const pbUpdates = { updatedAt: new Date().toISOString() };
        if (updates.content !== undefined) pbUpdates.contentMarkdown = updates.content;
        if (updates.status) pbUpdates.status = mapStatusToPocketBase(updates.status);
        if (updates.changeSummary) pbUpdates.changeSummary = updates.changeSummary;
        const record = await client.collection('legal_versions').update(versionId, pbUpdates);
        return mapVersionFromPocketBase(record);
      } catch (error) {
        throw new Error(getPocketBaseErrorMessage(error));
      }
    }
    throw new Error('Backend not configured.');
  },

  verifyDocumentPersistence: async (documentId, versionId) => {
    if (!legalVaultService.isBackendConfigured()) return true;
    const client = await requirePocketBase('verifyDocumentPersistence');
    try {
      const doc = await client.collection('legal_documents').getOne(documentId);
      const ver = await client.collection('legal_versions').getOne(versionId);
      if (!doc || !ver) throw new Error('Record not found after save.');
      return true;
    } catch (error) {
      throw new Error(`Persistence verification failed: ${getPocketBaseErrorMessage(error)}`);
    }
  },

  deactivatePublicLinksForDocument: async (documentId) => {
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('deactivatePublicLinksForDocument');
      try {
        const links = await client.collection('legal_public_links').getFullList({
          filter: `documentId="${documentId}" && isActive=true`,
        });
        const now = new Date().toISOString();
        for (const link of links) {
          await client.collection('legal_public_links').update(link.id, {
            isActive: false,
            disabledReason: 'Document is not publicly available.',
            disabledAt: now,
            updatedAt: now,
          });
        }
      } catch (error) {
        console.error('Error deactivating public links:', error);
      }
    }
  },

  ensurePublicLinkForDocument: async (documentId) => {
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('ensurePublicLinkForDocument');
      try {
        await requireFreshAuth(['owner', 'admin']);
        const document = await client.collection('legal_documents').getOne(documentId);
        if (document.status !== 'published') {
          throw new Error('Publish this document as Public or Unlisted before copying its link.');
        }
        if (document.visibility !== 'public' && document.visibility !== 'unlisted') {
          throw new Error('Publish this document as Public or Unlisted before copying its link.');
        }
        const existingLinks = await client.collection('legal_public_links').getFullList({
          filter: `documentId="${documentId}"`,
        });
        const now = new Date().toISOString();
        const linkData = {
          documentId,
          slug: document.slug,
          isActive: true,
          requireEmailForDownload: false,
          allowPdf: true,
          allowWord: true,
          allowPrint: true,
          archivedVisible: false,
          updatedAt: now,
        };
        if (existingLinks.length > 0) {
          const canonicalLink =
            existingLinks.find((link) => link.slug === document.slug) || existingLinks[0];
          const record = await client.collection('legal_public_links').update(canonicalLink.id, linkData);
          for (const duplicateLink of existingLinks) {
            if (duplicateLink.id === canonicalLink.id || duplicateLink.isActive === false) continue;
            await client.collection('legal_public_links').update(duplicateLink.id, {
              isActive: false,
              disabledReason: 'Superseded duplicate public link.',
              disabledAt: now,
              updatedAt: now,
            });
          }
          return mapPublicLink(record);
        } else {
          const record = await client.collection('legal_public_links').create({ ...linkData, createdAt: now });
          return mapPublicLink(record);
        }
      } catch (error) {
        throw new Error(getPocketBaseErrorMessage(error));
      }
    }
    throw new Error('Backend not configured.');
  },

  publishVersion: async (versionId, publishData) => {
    if (!legalVaultService.isBackendConfigured()) throw new Error('Backend not configured.');
    const client = await requirePocketBase('publishVersion');
    try {
      const publishingUser = await requireFreshAuth(['owner', 'admin']);
      const sourceVersion = await client.collection('legal_versions').getOne(versionId);
      if (!sourceVersion) {
        throw new Error('Version could not be found.');
      }
      const isResumingPublishedCurrent = sourceVersion.status === 'published' && sourceVersion.isCurrent === true;
      if (sourceVersion.status !== 'draft' && !isResumingPublishedCurrent) {
        throw new Error('Only a saved Draft version can be published.');
      }

      const document = await client.collection('legal_documents').getOne(sourceVersion.documentId);
      if (!document) {
        throw new Error('Document could not be found.');
      }

      const contentMarkdown = sourceVersion.contentMarkdown || '';
      if (!contentMarkdown.trim()) {
        throw new Error('Published terms cannot be empty.');
      }
      const requestedEffectiveDate = toPocketBaseDateTime(publishData.effectiveDate);
      const effectiveDate =
        isResumingPublishedCurrent && sourceVersion.effectiveDate
          ? sourceVersion.effectiveDate
          : requestedEffectiveDate;
      if (!effectiveDate) {
        throw new Error('A valid effective date is required.');
      }
      const targetVisibility = mapVisibilityToPocketBase(
        publishData.visibility || mapVisibilityFromPocketBase(document.visibility) || 'Private'
      );
      if (!['public', 'unlisted', 'private', 'password_protected'].includes(targetVisibility)) {
        throw new Error('Invalid publication visibility.');
      }
      const requestedChangeSummary = publishData.changeSummary || '';
      const changeSummary =
        isResumingPublishedCurrent && sourceVersion.changeSummary
          ? sourceVersion.changeSummary
          : requestedChangeSummary;
      if (changeSummary.length > 5000) {
        throw new Error('The change summary is too long.');
      }

      const contentHash = await sha256Hex(contentMarkdown);
      if (
        isResumingPublishedCurrent &&
        sourceVersion.contentHash &&
        sourceVersion.contentHash !== contentHash
      ) {
        throw new Error('Publication recovery stopped because the stored content hash does not match the draft content.');
      }
      const now = new Date().toISOString();
      const publishedAt =
        isResumingPublishedCurrent && sourceVersion.publishedAt ? sourceVersion.publishedAt : now;
      const otherVersions = await client.collection('legal_versions').getFullList({
        filter: `documentId="${sourceVersion.documentId}" && id!="${versionId}" && (status="published" || isCurrent=true)`,
      });

      for (const previousVersion of otherVersions) {
        await client.collection('legal_versions').update(previousVersion.id, {
          status: 'superseded',
          archivedAt: now,
          isCurrent: false,
          isImmutable: true,
          updatedAt: now,
        });
      }

      const existingMetadata =
        sourceVersion.metadata && typeof sourceVersion.metadata === 'object' && !Array.isArray(sourceVersion.metadata)
          ? sourceVersion.metadata
          : {};
      const updatedVersion = await client.collection('legal_versions').update(versionId, {
        status: 'published',
        effectiveDate,
        publishedAt,
        changeSummary,
        contentHash,
        isCurrent: true,
        isImmutable: true,
        metadata: {
          ...existingMetadata,
          publication: {
            actorName: publishingUser?.name || 'BPA Admin',
            actorEmail: publishingUser?.email || '',
            publishedAt,
            contentHash,
          },
        },
        updatedAt: now,
      });

      await client.collection('legal_documents').update(sourceVersion.documentId, {
        status: 'published',
        visibility: targetVisibility,
        updatedAt: now,
      });

      if (targetVisibility === 'public' || targetVisibility === 'unlisted') {
        await legalVaultService.ensurePublicLinkForDocument(sourceVersion.documentId);
      } else {
        await legalVaultService.deactivatePublicLinksForDocument(sourceVersion.documentId);
      }

      const verificationVersions = await client.collection('legal_versions').getFullList({
        filter: `documentId="${sourceVersion.documentId}" && isCurrent=true`,
      });
      if (verificationVersions.length !== 1 || verificationVersions[0].id !== versionId) {
        throw new Error('Publication verification failed: exactly one current version was not confirmed.');
      }
      if (verificationVersions[0].contentHash !== contentHash) {
        throw new Error('Publication verification failed: the stored content hash does not match.');
      }

      return mapVersionFromPocketBase(updatedVersion);
    } catch (error) {
      throw new Error(getPocketBaseErrorMessage(error));
    }
  },

  cleanupFailedNewDocument: async (documentId) => {
    if (!legalVaultService.isBackendConfigured()) {
      return { deleted: false, reason: 'Backend not configured.' };
    }
    const client = await requirePocketBase('cleanupFailedNewDocument');
    try {
      await requireFreshAuth(['owner', 'admin']);
      return await client.send('/api/bpa-legal-vault/cleanup-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentId }),
      });
    } catch (error) {
      throw new Error(getPocketBaseErrorMessage(error));
    }
  },

  getPublicLinks: async () => {
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('getPublicLinks');
      try {
        const records = await client.collection('legal_public_links').getFullList({
          sort: '-createdAt',
          expand: 'documentId',
        });
        return records.map((record) => {
          const doc = record.expand?.documentId;
          const link = mapPublicLink(record);
          return {
            ...link,
            title: doc?.title || 'Unknown',
            visibility: doc ? mapVisibilityFromPocketBase(doc.visibility) : 'Unknown',
          };
        });
      } catch (error) {
        return [];
      }
    }
    return [];
  },

  updatePublicLink: async (linkId, updates) => {
    if (!legalVaultService.isBackendConfigured()) {
      throw new Error('Backend not configured.');
    }
    const client = await requirePocketBase('updatePublicLink');
    try {
      await requireFreshAuth(['owner', 'admin']);
      const payload = { updatedAt: new Date().toISOString() };
      if (typeof updates.isActive === 'boolean') {
        payload.isActive = updates.isActive;
        if (updates.isActive) {
          payload.disabledReason = '';
          payload.disabledAt = '';
        } else {
          payload.disabledReason = 'Disabled by administrator.';
          payload.disabledAt = new Date().toISOString();
        }
      }
      if (updates.expiresAt !== undefined) {
        payload.expiresAt = updates.expiresAt ? toPocketBaseDateTime(updates.expiresAt) : '';
      }
      const record = await client.collection('legal_public_links').update(linkId, payload);
      return mapPublicLink(record);
    } catch (error) {
      throw new Error(getPocketBaseErrorMessage(error));
    }
  },

  addAuditEntry: async (entryData) => {
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('addAuditEntry');
      try {
        const user = legalVaultService.getCurrentUser();
        const mappedAction = auditActionToPocketBase[entryData.action];
        if (!mappedAction) {
          console.warn('Unmapped audit action:', entryData.action);
          return null;
        }
        const now = new Date().toISOString();
        return await client.collection(AUDIT_COLLECTION).create({
          documentId: entryData.documentId || '',
          versionId: entryData.versionId || '',
          action: mappedAction,
          actorName: user?.name || entryData.actor || 'System',
          actorEmail: user?.email || '',
          snapshot: {
            documentTitle: entryData.documentTitle || '',
            version: entryData.version || '',
            details: entryData.details || {},
          },
          createdAt: now,
        });
      } catch (error) {
        console.warn('Audit log failure:', error);
      }
    }
    return null;
  },

  getAuditTrail: async (documentId) => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const client = await requirePocketBase('getAuditTrail');
        const records = await client.collection(AUDIT_COLLECTION).getFullList({
          filter: `documentId="${documentId}"`,
          sort: '-createdAt',
        });
        return records.map((record) => ({
          id: record.id,
          timestamp: record.createdAt,
          action: auditActionFromPocketBase[record.action] || record.action,
          documentTitle: record.snapshot?.documentTitle || '',
          version: record.snapshot?.version || '',
          actor: record.actorName || record.actorEmail || 'System',
          details: record.snapshot?.details || {},
        }));
      } catch (error) {
        return [];
      }
    }
    return [];
  },

  getTemplates: async () => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const client = await requirePocketBase('getTemplates');
        const records = await client.collection('legal_templates').getFullList({ sort: '-updatedAt' });
        return records.map(mapTemplateFromPocketBase);
      } catch (error) {
        return [];
      }
    }
    return [];
  },

  getTemplateById: async (templateId) => {
    if (!legalVaultService.isBackendConfigured()) {
      throw new Error('Backend not configured.');
    }
    const client = await requirePocketBase('getTemplateById');
    try {
      const record = await client.collection('legal_templates').getOne(templateId);
      return mapTemplateFromPocketBase(record);
    } catch (error) {
      throw new Error(getPocketBaseErrorMessage(error));
    }
  },

  createTemplate: async (templateData) => {
    if (!legalVaultService.isBackendConfigured()) {
      throw new Error('Backend not configured.');
    }
    const client = await requirePocketBase('createTemplate');
    try {
      await requireFreshAuth(['owner', 'admin', 'editor']);
      if (!templateData.name?.trim()) {
        throw new Error('Template title is required.');
      }
      if (!templateData.type) {
        throw new Error('Template document type is required.');
      }
      const now = new Date().toISOString();
      const payload = {
        title: String(templateData.name || '').trim(),
        documentType: documentTypeMapToPocketBase[templateData.type] || templateData.type,
        description: templateData.description || '',
        starterContentHtml: templateData.starterContent || '',
        tags: Array.isArray(templateData.tags)
          ? templateData.tags
          : String(templateData.tags || '')
              .split(',')
              .map((tag) => tag.trim())
              .filter(Boolean),
        isActive: templateData.isActive !== false,
        createdAt: now,
        updatedAt: now,
      };
      if (templateData.brandId) {
        payload.brandId = templateData.brandId;
      }
      const record = await client.collection('legal_templates').create(payload);
      return mapTemplateFromPocketBase(record);
    } catch (error) {
      throw new Error(getPocketBaseErrorMessage(error));
    }
  },

  duplicateTemplate: async (templateId) => {
    const template = await legalVaultService.getTemplateById(templateId);
    if (!template) {
      throw new Error('Template could not be found.');
    }
    return legalVaultService.createTemplate({
      ...template,
      name: `${template.name} (Copy)`,
    });
  },

  getBrandingProfiles: async () => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const client = await requirePocketBase('getBrandingProfiles');
        const records = await client.collection('legal_branding_profiles').getFullList({ sort: '-createdAt' });
        return records.map(normalizeBrand);
      } catch (error) {
        return [];
      }
    }
    return [];
  },

  getBrandingProfileById: async (id) => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const client = await requirePocketBase('getBrandingProfileById');
        const record = await client.collection('legal_branding_profiles').getOne(id);
        return normalizeBrand(record);
      } catch (error) {
        return null;
      }
    }
    return null;
  },

  updateBrandingProfile: async (id, updates) => {
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('updateBrandingProfile');
      try {
        await requireFreshAuth(['owner', 'admin']);
        const formData = new FormData();
        Object.keys(updates).forEach((key) => {
          if (key === 'logo' && updates[key] instanceof File) {
            formData.append(key, updates[key]);
          } else if (key === 'removeLogo' && updates[key] === true) {
            formData.append('logo', '');
          } else if (updates[key] !== undefined && updates[key] !== null && key !== 'removeLogo') {
            formData.append(key, updates[key]);
          }
        });
        const record = await client.collection('legal_branding_profiles').update(id, formData);
        return normalizeBrand(record);
      } catch (error) {
        throw new Error(getPocketBaseErrorMessage(error));
      }
    }
    return null;
  },

  getGlobalSettings: async () => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const client = await requirePocketBase('getGlobalSettings');
        const records = await client.collection('legal_settings').getFullList({
          filter: 'key="global_defaults"',
        });
        if (records.length > 0) return records[0].value || {};
        return {};
      } catch (error) {
        return {};
      }
    }
    return {};
  },

  updateGlobalSettings: async (updates) => {
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('updateGlobalSettings');
      try {
        await requireFreshAuth(['owner', 'admin']);
        const records = await client.collection('legal_settings').getFullList({
          filter: 'key="global_defaults"',
        });
        if (records.length > 0) {
          return await client.collection('legal_settings').update(records[0].id, { value: updates });
        } else {
          return await client.collection('legal_settings').create({ key: 'global_defaults', scope: 'global', value: updates });
        }
      } catch (error) {
        throw new Error(getPocketBaseErrorMessage(error));
      }
    }
    return {};
  },

  getBrandingProfileForDocument: async (document) => {
    if (document.brandId) {
      const brand = await legalVaultService.getBrandingProfileById(document.brandId);
      if (brand && brand.isActive !== false) return brand;
    }
    return await legalVaultService.getDefaultBrandingProfile();
  },

  getDefaultBrandingProfile: async () => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const client = await requirePocketBase('getDefaultBrandingProfile');
        let records = await client.collection('legal_branding_profiles').getFullList({
          filter: 'isDefault=true && isActive=true',
        });
        if (records.length > 0) return normalizeBrand(records[0]);
        records = await client.collection('legal_branding_profiles').getFullList({
          filter: 'isActive=true',
        });
        if (records.length > 0) return normalizeBrand(records[0]);
        return null;
      } catch (error) {
        return null;
      }
    }
    return null;
  },

  ensureDefaultBrandingProfile: async (seed = {}) => {
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('ensureDefaultBrandingProfile');
      try {
        await requireFreshAuth(['owner', 'admin']);
        let records = await client.collection('legal_branding_profiles').getFullList({
          filter: 'isDefault=true && isActive=true',
        });
        if (records.length > 0) {
          return normalizeBrand(records[0]);
        }
        records = await client.collection('legal_branding_profiles').getFullList({
          filter: 'isActive=true',
        });
        if (records.length > 0) {
          const updatedRecord = await client
            .collection('legal_branding_profiles')
            .update(records[0].id, { isDefault: true, updatedAt: new Date().toISOString() });
          return normalizeBrand(updatedRecord);
        }
        const now = new Date().toISOString();
        const newRecord = await client.collection('legal_branding_profiles').create({
          name: seed.businessName || seed.tradingName || 'BPA Solutions',
          tradingName: seed.tradingName || '',
          abn: seed.abn || '',
          acn: seed.acn || '',
          website: seed.websiteUrl || '',
          phone: seed.phone || '',
          legalEmail: seed.legalEmail || '',
          supportEmail: seed.supportEmail || '',
          address: seed.address || '',
          footerDisclaimer: seed.footerDisclaimer || '',
          isDefault: true,
          isActive: true,
          createdAt: now,
          updatedAt: now,
        });
        return normalizeBrand(newRecord);
      } catch (error) {
        throw new Error(getPocketBaseErrorMessage(error));
      }
    }
    throw new Error('Backend not configured.');
  },

  getDashboardStats: async () => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const docs = await legalVaultService.getDocuments();
        return {
          total: docs.length,
          draft: docs.filter((d) => d.status === 'Draft').length,
          published: docs.filter((d) => d.status === 'Published').length,
          archived: docs.filter((d) => d.status === 'Archived').length,
          dueForReview: docs.filter((d) => d.reviewDate && new Date(d.reviewDate) < new Date()).length,
          scheduled: 0,
        };
      } catch (error) {
        return { total: 0, draft: 0, published: 0, archived: 0, dueForReview: 0, scheduled: 0 };
      }
    }
    return { total: 0, draft: 0, published: 0, archived: 0, dueForReview: 0, scheduled: 0 };
  },

  getExportsHistory: async () => {
    if (legalVaultService.isBackendConfigured()) {
      try {
        const client = await requirePocketBase('getExportsHistory');
        const records = await client.collection('legal_exports').getFullList({
          sort: '-exportedAt',
          expand: 'documentId,versionId',
        });
        return records.map((r) => ({
          id: r.id,
          title: r.expand?.documentId?.title || 'Unknown',
          version: r.expand?.versionId?.versionNumber || '—',
          type: r.exportType,
          date: new Date(r.exportedAt).toLocaleString(),
          status: r.status,
        }));
      } catch (error) {
        return [];
      }
    }
    return [];
  },

  createExportRecord: async (exportData) => {
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('createExportRecord');
      try {
        return await client.collection('legal_exports').create(exportData);
      } catch (error) {
        console.warn('Export record failure:', error);
      }
    }
    return null;
  },

  createDownloadRequest: async (requestData) => {
    if (legalVaultService.isBackendConfigured()) {
      const client = await requirePocketBase('createDownloadRequest');
      try {
        if (!requestData.documentId || !requestData.versionId || !requestData.fullName || !requestData.email || !requestData.requestType) {
          throw new Error('Missing required fields for download request.');
        }
        const now = new Date().toISOString();
        const record = await client.collection('legal_download_requests').create({
          documentId: requestData.documentId,
          versionId: requestData.versionId,
          fullName: requestData.fullName,
          email: requestData.email,
          companyName: requestData.companyName || '',
          requestType: requestData.requestType,
          status: 'queued',
          requestedAt: now,
          userAgent: navigator.userAgent || '',
          requestSnapshot: {
            source: 'public_legal_hub',
          },
        });
        return record;
      } catch (error) {
        throw new Error(getPocketBaseErrorMessage(error));
      }
    }
    throw new Error('Backend not configured.');
  },
};